import React, { useContext, useMemo } from 'react';

import { StyleSheet, Text, View } from 'react-native';

import { StoreLocationMap } from 'components/store-location-map';
import { IMarker } from 'components/store-location-map/types';
import { IconStyled } from 'components/top-service-mode/styled';
import { ActionButton } from 'components/ui/action-button';
import { getMarkerTypeForAlias } from 'hooks/use-map/utils';
import { t } from 'state/intl/translations';
import { useOrderContext } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';
import { SmartAlertsContext } from 'state/smart-alert';
import { primitive } from 'styles/constants/primitives';

import { useCheckoutLoadingState, useStoreLocatorWithMap } from '../../hooks';
import { WidgetBaseStyles } from '../../pages/styled';
import { ChangeStoreConfirmationDialog } from '../change-store-confirmation-dialog';

export const StoreLocatorWithMapWidget = () => {
  const {
    addressLine1,
    cityStateZip,
    isLoadingDistance,
    distanceText,
    canShowDistance,
    restaurantCoordinates,
    handleChangeStore,
    dialogProps,
  } = useStoreLocatorWithMap();
  const { isCheckoutLoading } = useCheckoutLoadingState();

  const { deliveryAddress } = useOrderContext();
  const { isDelivery } = useServiceModeContext();

  const { tsmSmartAlert } = useContext(SmartAlertsContext);

  const markers = useMemo(() => {
    const markersList: IMarker[] = [];

    if (isDelivery && deliveryAddress?.latitude && deliveryAddress?.longitude) {
      const deliveryMarkerType = getMarkerTypeForAlias(deliveryAddress?.alias);
      markersList.push({
        id: 'delivery',
        type: deliveryMarkerType,
        location: {
          lat: deliveryAddress.latitude,
          lng: deliveryAddress.longitude,
        },
      });
    }

    return markersList;
  }, [isDelivery, deliveryAddress]);

  return (
    <View>
      <View style={styles.mapContainer}>
        {restaurantCoordinates && (
          <StoreLocationMap
            storeLocation={restaurantCoordinates}
            markers={markers}
            showUserMarker={!isDelivery}
          />
        )}
      </View>
      {tsmSmartAlert && (
        <View style={styles.smartAlertContainer}>
          <IconStyled variant="warning" />
          <Text style={styles.distanceText}>{tsmSmartAlert.message}</Text>
        </View>
      )}
      <View style={styles.container}>
        <View style={styles.addressContainer}>
          <Text style={styles.addressText}>{addressLine1}</Text>
          <View style={styles.space4} />
          <Text style={styles.addressText}>
            {cityStateZip}
            {!isLoadingDistance && distanceText && canShowDistance && (
              <Text style={styles.distanceText}>{` | ${distanceText}`}</Text>
            )}
          </Text>
        </View>
        <ActionButton
          onPress={handleChangeStore}
          testID="change-store-button"
          accessibilityLabel={t('changeStore', 'Change Store')}
          disabled={isCheckoutLoading}
        >
          {t('change')}
        </ActionButton>
      </View>
      <ChangeStoreConfirmationDialog {...dialogProps} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    ...WidgetBaseStyles.container,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  },
  smartAlertContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    height: 32,
    backgroundColor: primitive.bk.$errorLight,
  },
  addressText: {
    fontSize: 14,
    lineHeight: 16,
    fontWeight: 'normal',
    fontFamily: primitive.fontFamily.$body,
    color: primitive.bk.$bbqBrown,
  },
  distanceText: {
    fontWeight: 'bold',
    fontFamily: primitive.fontFamily.$headers,
    color: primitive.bk.$bbqBrown,
  },
  mapContainer: {
    minHeight: 160,
    flexDirection: 'row',
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    overflow: 'hidden',
  },
  addressContainer: {
    flex: 1,
    paddingRight: 16,
  },
  space4: {
    marginTop: 4,
  },
});
